import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ShoppingBag } from 'lucide-react';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';
import useThemeStore from '../store/themeStore';
import useAuthStore from '../store/authStore';
import ContactForm from '../components/ContactForm';
import CatalogsSection from '../components/CatalogsSection';
import CouponPromoSection from '../components/CouponPromoSection';
import SeasonalLandingSection from '../components/SeasonalLandingSection';
import EditableText from '../components/editor/EditableText';
import EditableImage from '../components/editor/EditableImage';
import WelcomeSection from '../components/home/WelcomeSection';
import DiscountSection from '../components/home/DiscountSection';
import FeaturedProductsSection from '../components/home/FeaturedProductsSection';
import SaleBannerSection from '../components/SaleBannerSection';
import PWAInstallSection from '../components/home/PWAInstallSection';
import HaargummiShowcase from '../components/home/HaargummiShowcase';
import BlogTeaserSection from '../components/home/BlogTeaserSection';
import LimitedCollectionsSection from '../components/LimitedCollectionsSection';
import CollectionCategoriesShowcase from '../components/CollectionCategoriesShowcase';
import SeasonalHeroSection from '../components/SeasonalHeroSection';
import AppDownloadButton from '../components/AppDownloadButton';
import { imgVariant } from '../utils/imgUrl';

const Home = () => {
  const [visibility, setVisibility] = useState({ show_collections: false, show_seasonal_landing: false });
  useEffect(() => {
    axiosInstance.get('/settings/homepage-visibility').then((r) => setVisibility(r.data)).catch(() => {});
  }, []);
  const { branding, activeTheme } = useThemeStore();
  const { isAuthenticated } = useAuthStore();
  const [showNewsletterPopup, setShowNewsletterPopup] = useState(false);
  const [newsletterData, setNewsletterData] = useState({ email: '', name: '', consent: false });
  const [discountCode, setDiscountCode] = useState('');
  const [generatedDiscountCode, setGeneratedDiscountCode] = useState('');
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [partnerSites, setPartnerSites] = useState([]);
  const [shopAdvantages, setShopAdvantages] = useState([]);
  const [newsletterPopupSettings, setNewsletterPopupSettings] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstallable, setIsAppInstallable] = useState(false);

  useEffect(() => {
    const fetchNewsletterSettings = async () => {
      try {
        const response = await axiosInstance.get('/settings/newsletter-popup-settings');
        setNewsletterPopupSettings(response.data);
        
        // Show popup if enabled and not seen — and only for guests, so
        // we don't double-up on the Neukunden-Coupons that registered users
        // already get automatically. Also skip if Cookie-Banner is still
        // shown so we don't stack two popups on Mobile (cuts off text).
        const hasSeenPopup = localStorage.getItem('newsletter-popup-seen');
        const hasAcceptedCookies = localStorage.getItem('cookie-consent');
        const shouldShow = !hasSeenPopup && response.data.is_enabled && !isAuthenticated && hasAcceptedCookies;
        if (shouldShow) {
          const delay = (response.data.show_delay_seconds || 6) * 1000;
          const timer = setTimeout(() => {
            setShowNewsletterPopup(true);
          }, delay);
          return () => clearTimeout(timer);
        }
        // If cookies not yet accepted, retry once the user interacts (poll briefly)
        if (!hasSeenPopup && response.data.is_enabled && !isAuthenticated && !hasAcceptedCookies) {
          const poll = setInterval(() => {
            if (localStorage.getItem('cookie-consent')) {
              clearInterval(poll);
              const delay = (response.data.show_delay_seconds || 6) * 1000;
              setTimeout(() => setShowNewsletterPopup(true), delay);
            }
          }, 1500);
          // Stop polling after 5 min — at that point the user has either
          // accepted or is intentionally ignoring the banner.
          setTimeout(() => clearInterval(poll), 5 * 60 * 1000);
          return () => clearInterval(poll);
        }
      } catch (error) {
        console.error('Error fetching newsletter popup settings:', error);
      }
    };
    
    fetchNewsletterSettings();
  }, [isAuthenticated]);

  // PWA Install Prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsAppInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsAppInstallable(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, discountRes, partnerSitesRes, advantagesRes] = await Promise.all([
          axiosInstance.get('/products'),
          axiosInstance.get('/categories?hide_empty=true'),
          axiosInstance.get('/public-discount-code').catch(() => ({ data: { code: 'WELCOME20' } })),
          axiosInstance.get('/partner-sites').catch(() => ({ data: [] })),
          axiosInstance.get('/shop-advantages').catch(() => ({ data: [] }))
        ]);
        
        // Check if admin has selected featured products
        const savedFeatured = localStorage.getItem('featured-products');
        if (savedFeatured) {
          const featuredIds = JSON.parse(savedFeatured);
          const featured = productsRes.data.filter(p => featuredIds.includes(p.id));
          setFeaturedProducts(featured.slice(0, 12));
        } else {
          // Default: show first 12 products
          setFeaturedProducts(productsRes.data.slice(0, 12));
        }
        
        setCategories(categoriesRes.data);
        setDiscountCode(discountRes.data?.code || 'WELCOME20');
        setPartnerSites(partnerSitesRes.data || []);
        setShopAdvantages(advantagesRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleInstallApp = async () => {
    if (!deferredPrompt) {
      // Fallback for iOS or when prompt is not available
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        toast.info('Tippen Sie auf das Teilen-Symbol und dann auf "Zum Home-Bildschirm"');
      } else {
        toast.info('Installation nicht verfügbar. Öffnen Sie die Seite in Chrome, Edge oder Safari.');
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App wird installiert! 🎉');
      setIsAppInstallable(false);
    }
    
    setDeferredPrompt(null);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterData.consent) {
      toast.error('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }

    try {
      const response = await axiosInstance.post('/newsletter/subscribe', {
        email: newsletterData.email,
        name: newsletterData.name,
        consent_given: newsletterData.consent,
      });

      if (response.data?.discount_code) {
        // Legacy path (Single-Opt-In fallback) — keep for compatibility.
        setGeneratedDiscountCode(response.data.discount_code);
        toast.success('Erfolgreich angemeldet! Ihr Rabattcode: ' + response.data.discount_code);
      } else {
        toast.success('Bitte prüfen Sie Ihr Postfach und bestätigen Sie die Anmeldung.');
      }

      localStorage.setItem('newsletter-popup-seen', 'true');
      if (!response.data?.discount_code) {
        setTimeout(() => setShowNewsletterPopup(false), 2500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anmelden');
    }
  };

  const categoryIcons = {
    'Alle Produkte': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4509267066.jpg',
    'Dekoration': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4521920725.jpg',
    'Handgemachte Seifen': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4502023151.jpg',
    'Accessoires': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4509267066.jpg',
    'Schmuck': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4505090058.jpg',
    'Aufbewahrung/Organisation': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4521928104.jpg',
    'Handgemachte Kerzen': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4521928010.jpg',
    'Schlüsselanhänger': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4521971635.jpg',
    'Handtaschen': 'https://d2j6dbq0eux0bg.cloudfront.net/images/97452253/4554470096.jpg'
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: activeTheme.colors.background }}>
      {/* App Download Button - Floating */}
      <AppDownloadButton />
      
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 px-4 sm:px-6 lg:px-8" style={{ 
        background: `linear-gradient(135deg, ${activeTheme.colors.primary} 0%, ${activeTheme.colors.secondary} 50%, ${activeTheme.colors.tertiary} 100%)`
      }}>
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-4">{branding.shopName}</h1>
          <p className="text-base md:text-xl text-white/90 mb-2 md:mb-3">{branding.tagline}</p>
          {/* Brand slogan — emotional, distinct from the descriptive tagline */}
          <p
            className="text-sm md:text-lg italic text-white mb-6 md:mb-8 font-light tracking-wide"
            style={{ fontFamily: 'Georgia, serif' }}
            data-testid="home-hero-slogan"
          >
            „Mehr als shoppen – deine Me-Time beginnt hier."
          </p>
          <div className="max-w-xl mx-auto flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              placeholder="Produkte durchsuchen..."
              className="flex-1 px-4 py-3 rounded-md border-none text-sm md:text-base"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  window.location.href = `/products?search=${encodeURIComponent(e.target.value)}`;
                }
              }}
            />
            <Link to="/products" className="w-full sm:w-auto">
              <Button size="lg" className="px-6 md:px-8 w-full sm:w-auto" style={{ backgroundColor: '#6B5D4F', color: 'white' }}>Suchen</Button>
            </Link>
          </div>
          {/* Prominent shop CTA — brand-styled, matching the rest of the site */}
          <div className="mt-6 md:mt-8">
            <Link to="/products" data-testid="home-hero-shop-cta">
              <button
                type="button"
                className="inline-flex items-center gap-3 px-8 md:px-10 py-3.5 md:py-4 text-sm tracking-[2px] uppercase font-semibold transition-colors"
                style={{ backgroundColor: '#5D4E3C', color: '#F5EDE4', border: '1px solid #5D4E3C' }}
              >
                <ShoppingBag className="h-4 w-4" />
                <span>Jetzt entdecken</span>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      {/* Seasonal Hero Section - Full Width */}
      <SeasonalHeroSection theme={activeTheme?.seasonalTheme || 'standard'} showCollections={visibility.show_collections} />

      {/* SALE-Banner (rendert nur wenn es aktuell reduzierte Artikel gibt) */}
      <SaleBannerSection />

      {/* Bestseller strip — first thing buyers see after the hero */}
      <section className="py-8 md:py-10 px-4 sm:px-6 lg:px-8 bg-white" data-testid="home-bestseller-strip">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-3 mb-4 md:mb-6 flex-wrap">
            <div>
              <p className="text-[10px] sm:text-xs tracking-[4px] uppercase" style={{ color: '#C4A084' }}>
                Beliebt bei unseren Kundinnen
              </p>
              <h2
                className="text-2xl md:text-3xl font-bold text-[#5D4E3C] mt-1"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Direkt zum Shoppen
              </h2>
              <div className="w-12 h-[2px] mt-2" style={{ backgroundColor: '#C4A084' }} />
            </div>
            <Link to="/products" data-testid="home-bestseller-all">
              <Button
                size="sm"
                variant="outline"
                className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE]"
              >
                Alle Artikel ansehen →
              </Button>
            </Link>
          </div>
          <FeaturedProductsSection products={featuredProducts} />
        </div>
      </section>

      {/* Highlights / catalog section — soft cream background creates visual separation from white sections */}
      <section className="py-8 md:py-10 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#FAF5EE' }}>
        <div className="max-w-7xl mx-auto">
              {/* Welcome Box */}
              <WelcomeSection shopAdvantages={shopAdvantages} />

              {/* Discount Section */}
              <DiscountSection discountCode={discountCode} />

              {/* Haargummi-Kollektion */}
              <HaargummiShowcase />

              {/* Categories Section - Two Rows with Horizontal Scroll */}
              {categories.length > 0 && (
                <div className="mt-8 mb-8">
                  {/* Storytelling-Header für Vielfalt */}
                  <div className="mb-6">
                    <div
                      className="text-[10px] sm:text-xs tracking-[4px] mb-2"
                      style={{ color: '#C4A084' }}
                    >
                      UNSER SORTIMENT
                    </div>
                    <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
                      <h2
                        className="text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight"
                        style={{ color: '#5D4E3C', fontFamily: 'Georgia, serif' }}
                      >
                        Eine Welt voller Lieblingsstücke
                      </h2>
                      <Link
                        to="/categories"
                        className="text-xs sm:text-sm text-[#8B6F4D] hover:text-[#5D4E3C] underline whitespace-nowrap"
                      >
                        Alle Kategorien →
                      </Link>
                    </div>
                    <div className="w-12 h-px mb-3" style={{ background: '#C4A084' }} />
                    <p
                      className="text-xs sm:text-sm leading-relaxed max-w-2xl"
                      style={{ color: '#6D5A47' }}
                    >
                      Von <strong>handgemachten Seifen</strong> und{' '}
                      <strong>Schmuck</strong> über <strong>Federmäppchen</strong>,{' '}
                      <strong>Schminkpinsel</strong> und <strong>Kulturtaschen</strong>{' '}
                      bis zu <strong>Geldbeuteln</strong>, <strong>Schlüsselanhängern</strong>{' '}
                      und liebevoller <strong>Wohndeko</strong>: Bei Homeyoursoul findest du
                      Alltagsbegleiter mit Charakter – sorgfältig ausgewählt für die kleinen
                      und großen Momente.
                    </p>
                  </div>
                  {/* Horizontal scroll container for 2 rows */}
                  <div 
                    className="overflow-x-auto pb-4 scrollbar-hide" 
                    style={{ 
                      marginLeft: '-16px', 
                      marginRight: '-16px', 
                      paddingLeft: '16px',
                      paddingRight: '16px'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: 'max-content' }}>
                      {/* Row 1 */}
                      <div style={{ display: 'flex', gap: '12px' }}>
                        {categories.slice(0, Math.ceil(categories.length / 2)).map((category) => (
                          <Link
                            key={category.id || category.name}
                            to={`/products?category=${encodeURIComponent(category.name)}`}
                            style={{ flexShrink: 0 }}
                          >
                            <div 
                              className="relative overflow-hidden shadow-md hover:shadow-lg transition-all group"
                              style={{ width: '140px', height: '100px' }}
                            >
                              {category.image || categoryIcons[category.name] ? (
                                <img 
                                  src={imgVariant(category.image || categoryIcons[category.name], { w: 300 })} 
                                  alt={category.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-[#C4A084] to-[#8B7355]" />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-[#5D4E3C]/75 via-[#5D4E3C]/25 to-transparent" />
                              <div className="absolute bottom-0 left-0 right-0 p-2">
                                <p className="text-white text-xs font-medium text-center truncate">
                                  {category.name}
                                </p>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                      {/* Row 2 */}
                      <div style={{ display: 'flex', gap: '12px' }}>
                        {categories.slice(Math.ceil(categories.length / 2)).map((category) => (
                          <Link
                            key={category.id || category.name}
                            to={`/products?category=${encodeURIComponent(category.name)}`}
                            style={{ flexShrink: 0 }}
                          >
                            <div 
                              className="relative overflow-hidden shadow-md hover:shadow-lg transition-all group"
                              style={{ width: '140px', height: '100px' }}
                            >
                              {category.image || categoryIcons[category.name] ? (
                                <img 
                                  src={imgVariant(category.image || categoryIcons[category.name], { w: 300 })} 
                                  alt={category.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-[#C4A084] to-[#8B7355]" />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-[#5D4E3C]/75 via-[#5D4E3C]/25 to-transparent" />
                              <div className="absolute bottom-0 left-0 right-0 p-2">
                                <p className="text-white text-xs font-medium text-center truncate">
                                  {category.name}
                                </p>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
        </div>
      </section>

      {/* Blog Teaser Section - shows 3 latest blog posts with "Weiterlesen" */}
      <BlogTeaserSection />

      {/* App Download Section */}
      <PWAInstallSection 
        isAppInstallable={isAppInstallable}
        onInstall={handleInstallApp}
      />

      {/* Partner Sites Section */}
      {partnerSites.length > 0 && (
        <section className="py-12 px-4 sm:px-6 lg:px-8 mt-8" style={{ backgroundColor: '#F5EDE3' }}>
          <div className="max-w-6xl mx-auto">
            <h2 
              data-editable="partner-sites-title"
              className="text-2xl md:text-3xl font-bold text-center mb-3" 
              style={{ color: '#6D5A47' }}
            >
              Empfohlene Seiten zum Shoppen & Stöbern
            </h2>
            <p 
              data-editable="partner-sites-subtitle"
              className="text-center text-gray-600 mb-8"
            >
              Entdecken Sie unsere Partner und Kooperationen
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {partnerSites.map((site) => (
                <a
                  key={site.id}
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <div className="bg-white p-6 shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col items-center justify-center text-center">
                    {site.logo_url && (
                      <img
                        src={site.logo_url}
                        alt={site.name}
                        className="h-20 w-auto object-contain mb-4"
                      />
                    )}
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-[#B89968] transition-colors">
                      {site.name}
                    </h3>
                    {site.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {site.description}
                      </p>
                    )}
                    <div 
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium group-hover:scale-105 transition-transform"
                      style={{ background: 'linear-gradient(135deg, #C4A084 0%, #B89968 100%)' }}
                    >
                      Besuchen
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Seasonal Landing Pages Section */}
      {visibility.show_seasonal_landing && <SeasonalLandingSection />}

      {/* Coupon Promo Section */}
      <CouponPromoSection />

      {/* Catalogs Section */}
      <CatalogsSection />

      {/* Limited Collections */}
      {visibility.show_collections && <CollectionCategoriesShowcase />}

      {visibility.show_collections && <LimitedCollectionsSection />}

      {/* Newsletter Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white mt-8" data-editable-section="newsletter">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            data-editable="newsletter-title"
            className="text-3xl font-bold mb-6"
          >
            Jetzt zum Newsletter anmelden
          </h2>
          <form onSubmit={handleNewsletterSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Name*"
              required
              className="w-full px-4 py-3 border-2 rounded-md"
              style={{ borderColor: '#C4A084' }}
              value={newsletterData.name}
              onChange={(e) => setNewsletterData({ ...newsletterData, name: e.target.value })}
            />
            <input
              type="email"
              placeholder="E-Mail*"
              required
              className="w-full px-4 py-3 border-2 rounded-md"
              style={{ borderColor: '#C4A084' }}
              value={newsletterData.email}
              onChange={(e) => setNewsletterData({ ...newsletterData, email: e.target.value })}
            />
            <div className="flex items-start gap-2 text-left">
              <input
                type="checkbox"
                id="consent"
                checked={newsletterData.consent}
                onChange={(e) => setNewsletterData({ ...newsletterData, consent: e.target.checked })}
                className="mt-1"
                style={{ accentColor: '#B89968' }}
              />
              <label htmlFor="consent" className="text-sm text-gray-600">
                Ich bin damit einverstanden, dass diese Daten zum Zweck der Kontaktaufnahme gespeichert und verarbeitet werden. Mir ist bekannt, dass ich meine Einwilligung jederzeit widerrufen kann.*
              </label>
            </div>
            <Button 
              type="submit" 
              size="lg" 
              className="w-full text-white border-none"
              style={{ 
                background: 'linear-gradient(135deg, #C4A084 0%, #B89968 50%, #A88B5E 100%)'
              }}
            >
              Jetzt Anmelden
            </Button>
          </form>
        </div>
      </section>

      {/* Contact Form Section */}
      <ContactForm />

      {/* Newsletter Popup */}
      {showNewsletterPopup && (
        <div
          className="fixed z-50"
          style={{
            bottom: 'max(env(safe-area-inset-bottom, 0px), 12px)',
            right: 'max(env(safe-area-inset-right, 0px), 12px)',
            left: 'max(env(safe-area-inset-left, 0px), 12px)',
            maxWidth: '340px',
            marginLeft: 'auto',
            marginRight: 'auto',
            animation: 'slideUpFade 0.4s ease-out',
          }}
          data-testid="newsletter-popup"
        >
          <style>{`
            @keyframes slideUpFade {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @media (min-width: 640px) {
              [data-testid="newsletter-popup"] {
                left: auto !important;
                bottom: max(env(safe-area-inset-bottom, 0px), 24px) !important;
                right: max(env(safe-area-inset-right, 0px), 24px) !important;
              }
            }
          `}</style>
          <div
            className="relative bg-white shadow-lg overflow-hidden"
            style={{
              border: '1px solid #E8DCC8',
              borderTop: '3px solid #C4A084',
              borderRadius: '4px',
            }}
          >
            {/* Soft accent corner */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute', top: 0, left: 0, width: '90px', height: '90px',
                background: 'radial-gradient(circle at top left, rgba(196,160,132,0.10), transparent 70%)',
                pointerEvents: 'none',
              }}
            />

            {/* Close */}
            <button
              onClick={() => { setShowNewsletterPopup(false); localStorage.setItem('newsletter-popup-seen', 'true'); }}
              className="absolute top-2 right-2 text-[#A89A85] hover:text-[#5D4E3C] transition-colors p-1 z-10"
              aria-label="Schließen"
              data-testid="newsletter-popup-close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="p-4 sm:p-5">
              {generatedDiscountCode ? (
                <div className="text-center py-1">
                  <div className="text-[10px] tracking-[3px] mb-2" style={{ color: '#C4A084' }}>WILLKOMMEN</div>
                  <p className="text-sm mb-3" style={{ color: '#5D4E3C' }}>
                    Vielen Dank — Ihr persönlicher Code:
                  </p>
                  <div
                    className="py-3 px-2 mb-3 font-mono text-base tracking-widest"
                    style={{ background: '#FAF5EE', color: '#5D4E3C', border: '1px dashed #C4A084' }}
                  >
                    {generatedDiscountCode}
                  </div>
                  <p className="text-[11px] mb-3" style={{ color: '#8B7355' }}>
                    Einmalig einlösbar · 30 Tage gültig
                  </p>
                  <Button
                    onClick={() => setShowNewsletterPopup(false)}
                    className="rounded-none w-full text-xs"
                    style={{ background: '#5D4E3C', color: 'white' }}
                  >
                    Jetzt einkaufen
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <div className="flex items-start gap-3">
                    {/* Kleines, dezentes Briefumschlag-Icon */}
                    <div
                      className="flex-shrink-0 flex items-center justify-center"
                      style={{
                        width: '40px', height: '40px',
                        background: '#FAF5EE',
                        border: '1px solid #E8DCC8',
                        borderRadius: '50%',
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="#C4A084" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    </div>
                    <div className="min-w-0 pr-5">
                      <div className="text-[10px] tracking-[2px] mb-0.5" style={{ color: '#C4A084' }}>HOMEYOURSOUL</div>
                      <h3 className="text-sm font-semibold leading-tight" style={{ color: '#5D4E3C' }}>
                        {newsletterPopupSettings?.title || 'Bleiben Sie inspiriert'}
                      </h3>
                      <p className="text-xs mt-1 leading-snug" style={{ color: '#8B7355' }}>
                        Neue Kollektionen &amp; kleine Geschichten aus der Werkstatt — gelegentlich, nie aufdringlich.
                      </p>
                    </div>
                  </div>

                  <input
                    type="email"
                    placeholder="Ihre E-Mail-Adresse"
                    required
                    className="w-full px-3 py-2 text-sm border rounded-none focus:outline-none focus:border-[#C4A084]"
                    style={{ borderColor: '#D4C4B0', background: '#fff' }}
                    value={newsletterData.email}
                    onChange={(e) => setNewsletterData({ ...newsletterData, email: e.target.value })}
                    data-testid="newsletter-popup-email"
                  />

                  <label className="flex items-start gap-2 text-[10px] leading-snug cursor-pointer" style={{ color: '#8B7355' }}>
                    <input
                      type="checkbox"
                      checked={newsletterData.consent}
                      onChange={(e) => setNewsletterData({ ...newsletterData, consent: e.target.checked })}
                      className="mt-0.5 flex-shrink-0"
                      style={{ accentColor: '#C4A084' }}
                    />
                    <span>Ich bin mit der Verarbeitung meiner Daten einverstanden. Jederzeit widerrufbar.</span>
                  </label>

                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      type="submit"
                      className="flex-1 rounded-none text-xs"
                      style={{ background: '#5D4E3C', color: 'white' }}
                      data-testid="newsletter-popup-submit"
                    >
                      Anmelden
                    </Button>
                    <button
                      type="button"
                      onClick={() => { setShowNewsletterPopup(false); localStorage.setItem('newsletter-popup-seen', 'true'); }}
                      className="text-xs underline px-2"
                      style={{ color: '#8B7355' }}
                    >
                      Später
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;